import { useState, useEffect } from "react";
import axios from "axios";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from "recharts";
import {
  AlertTriangle, TrendingUp, Shield, Activity, Cpu, MemoryStick,
  HardDrive, RefreshCw, CheckCircle, Zap, Clock, ChevronRight
} from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityStyle = {
  critical: { text: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-400/20", bar: "#f87171" },
  high:     { text: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/20", bar: "#fb923c" },
  medium:   { text: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20", bar: "#fbbf24" },
  low:      { text: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/20", bar: "#60a5fa" },
};

const riskIcon = { critical: "🔴", high: "🟠", medium: "🟡", low: "🟢" };

function RiskSummaryCard({ label, value, severity, icon: Icon }) {
  const s = severityStyle[severity] || severityStyle.low;
  return (
    <div className={cn("bg-slate-900/50 border rounded-md p-4 animate-fade-in", s.border)}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={14} className={s.text} />
      </div>
      <p className={cn("text-3xl font-semibold", s.text)} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{value}</p>
    </div>
  );
}

function ProbabilityBar({ probability, severity }) {
  const color = severityStyle[severity]?.bar || "#94a3b8";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${probability * 100}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono text-slate-400 w-8 text-right">{Math.round(probability * 100)}%</span>
    </div>
  );
}

function FailurePredictionCard({ pred }) {
  const [expanded, setExpanded] = useState(false);
  const s = severityStyle[pred.severity] || severityStyle.medium;
  return (
    <div className={cn("bg-slate-900/50 border rounded-md overflow-hidden animate-fade-in", s.border)}>
      <button className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className={cn("px-2 py-0.5 rounded border text-[10px] font-semibold uppercase flex-shrink-0 mt-0.5", s.text, s.bg, s.border)}>
          {pred.severity}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-100 font-mono">{pred.resource}</p>
          <p className="text-xs text-slate-400 mt-0.5 truncate">{pred.reason}</p>
          <div className="mt-2">
            <ProbabilityBar probability={pred.probability} severity={pred.severity} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-[10px] text-slate-500">{pred.namespace}</span>
          <ChevronRight size={13} className={cn("text-slate-500 transition-transform", expanded && "rotate-90")} />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-800/60 space-y-3 animate-fade-in">
          <div className="mt-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Risk Signals</p>
            <div className="space-y-1">
              {pred.signals?.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                  <div className="w-1 h-1 rounded-full bg-amber-400 flex-shrink-0" />
                  {s}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Recommended Action</p>
            <p className="text-xs text-emerald-300 bg-emerald-400/5 border border-emerald-400/15 rounded px-3 py-2">
              {pred.recommended_action}
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-500">
            <Clock size={10} />
            <span>Predicted: {new Date(pred.predicted_failure_at).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AnomalyCard({ anomaly }) {
  const s = severityStyle[anomaly.severity] || severityStyle.low;
  const typeLabel = {
    memory_leak: "Memory Leak",
    cpu_throttling: "CPU Throttling",
    network_anomaly: "Network Anomaly",
  }[anomaly.type] || anomaly.type;
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase flex-shrink-0", s.text, s.bg, s.border)}>
          {typeLabel}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-200 font-mono">{anomaly.resource}</p>
          <p className="text-xs text-slate-400 mt-1">{anomaly.description}</p>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
            <span>Confidence: <span className="text-slate-400 font-mono">{Math.round(anomaly.confidence * 100)}%</span></span>
            <span>Detected: <span className="text-slate-400">{new Date(anomaly.detected_at).toLocaleTimeString()}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const isForecast = payload[0]?.payload?.is_forecast;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs">
      <p className="text-slate-400 mb-1">{label} {isForecast ? "(forecast)" : "(actual)"}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: <span className="font-mono">{p.value}%</span></p>
      ))}
    </div>
  );
};

export default function Predictive() {
  const { clusterId } = useCluster();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState("all");

  const fetch = () => {
    setLoading(true);
    axios.get(`${API}/clusters/${clusterId}/predictive`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, [clusterId]);

  if (loading || !data) {
    return (
      <div className="space-y-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-24 bg-slate-800/50 rounded-md animate-pulse" />
        ))}
      </div>
    );
  }

  const { risk_summary, failure_predictions, resource_exhaustion, capacity_forecast, anomalies, node_risks } = data;
  const overallS = severityStyle[risk_summary.overall_risk] || severityStyle.low;

  // Split trend into actual (historical) and forecast
  const actualTrend = capacity_forecast.trend.filter((d) => !d.is_forecast);
  const forecastTrend = capacity_forecast.trend.filter((d) => d.is_forecast);
  const chartData = capacity_forecast.trend;

  // Find where forecast starts
  const forecastStartIndex = chartData.findIndex((d) => d.is_forecast);
  const forecastStartDate = forecastStartIndex >= 0 ? chartData[forecastStartIndex].date : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Predictive Analytics
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            AI-powered failure prediction & capacity forecasting for <span className="font-mono text-slate-300">{clusterId}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold", overallS.text, overallS.bg, overallS.border)}>
            <Shield size={12} />
            Overall Risk: {risk_summary.overall_risk.toUpperCase()}
          </div>
          <button onClick={fetch} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Risk summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <RiskSummaryCard label="Total Predictions" value={risk_summary.total_predictions} severity="medium" icon={Activity} />
        <RiskSummaryCard label="Critical" value={risk_summary.critical} severity={risk_summary.critical > 0 ? "critical" : "low"} icon={AlertTriangle} />
        <RiskSummaryCard label="High Risk" value={risk_summary.high} severity={risk_summary.high > 0 ? "high" : "low"} icon={Zap} />
        <RiskSummaryCard label="Medium Risk" value={risk_summary.medium} severity={risk_summary.medium > 0 ? "medium" : "low"} icon={TrendingUp} />
      </div>

      {/* Capacity Forecast Chart */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Capacity Forecast (7d historical + 30d projection)
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Dashed area = model projection</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-950/50 rounded p-0.5">
            {["all", "cpu", "memory", "storage"].map((m) => (
              <button key={m} onClick={() => setActiveMetric(m)}
                className={cn("px-2 py-1 rounded text-[10px] font-medium capitalize transition-colors",
                  activeMetric === m ? "bg-slate-700 text-slate-100" : "text-slate-500 hover:text-slate-300")}>
                {m}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <defs>
              <pattern id="forecastPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                <rect width="3" height="6" fill="rgba(99,102,241,0.15)" />
              </pattern>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#475569" }} interval={Math.floor(chartData.length / 7)} />
            <YAxis tick={{ fontSize: 9, fill: "#475569" }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<CustomTooltip />} />
            {forecastStartDate && (
              <ReferenceLine x={forecastStartDate} stroke="#475569" strokeDasharray="4 3"
                label={{ value: "Today", fill: "#64748b", fontSize: 9, position: "top" }} />
            )}
            <Legend wrapperStyle={{ fontSize: 10, color: "#94a3b8" }} />
            {(activeMetric === "all" || activeMetric === "cpu") && (
              <Area type="monotone" dataKey="cpu" name="CPU" stroke="#6366f1" strokeWidth={2}
                fill="#6366f1" fillOpacity={0.08} />
            )}
            {(activeMetric === "all" || activeMetric === "memory") && (
              <Area type="monotone" dataKey="memory" name="Memory" stroke="#22d3ee" strokeWidth={2}
                fill="#22d3ee" fillOpacity={0.05} />
            )}
            {(activeMetric === "all" || activeMetric === "storage") && (
              <Area type="monotone" dataKey="storage" name="Storage" stroke="#fbbf24" strokeWidth={2}
                fill="#fbbf24" fillOpacity={0.04} />
            )}
          </AreaChart>
        </ResponsiveContainer>

        {/* Forecast summary pills */}
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-800">
          {capacity_forecast.summary.map((item) => {
            const icon = { CPU: Cpu, Memory: MemoryStick, Storage: HardDrive }[item.metric] || Activity;
            const Icon = icon;
            const exceed30d = item.forecast_30d > 90;
            return (
              <div key={item.metric} className="flex items-center gap-3">
                <Icon size={13} className="text-slate-500 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{item.metric}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs font-mono text-slate-400">{item.current}%</span>
                    <span className="text-slate-700">→</span>
                    <span className="text-xs font-mono text-amber-400">{item.forecast_7d}%</span>
                    <span className="text-slate-700">→</span>
                    <span className={cn("text-xs font-mono", exceed30d ? "text-rose-400" : "text-slate-300")}>{item.forecast_30d}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Failure Predictions */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-rose-400" />
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Failure Predictions
            </h3>
            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-rose-400/10 text-rose-400 border border-rose-400/20">
              {failure_predictions.length}
            </span>
          </div>
          {failure_predictions.length === 0 ? (
            <div className="bg-slate-900/50 border border-slate-800 rounded-md p-6 text-center">
              <CheckCircle size={24} className="mx-auto mb-2 text-emerald-400" />
              <p className="text-sm text-emerald-400">No failure predictions</p>
              <p className="text-xs text-slate-500 mt-1">All workloads appear healthy</p>
            </div>
          ) : (
            failure_predictions.map((pred) => <FailurePredictionCard key={pred.id} pred={pred} />)
          )}
        </div>

        {/* Right column: Resource Exhaustion + Node Risks */}
        <div className="space-y-4">
          {/* Resource Exhaustion */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <HardDrive size={14} className="text-amber-400" />
              <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Resource Exhaustion Timeline
              </h3>
            </div>
            {resource_exhaustion.length === 0 ? (
              <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 text-center text-sm text-emerald-400">
                <CheckCircle size={16} className="mx-auto mb-1" />
                No exhaustion predicted in 30 days
              </div>
            ) : (
              <div className="space-y-2">
                {resource_exhaustion.map((r, i) => {
                  const s = severityStyle[r.severity] || severityStyle.low;
                  return (
                    <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-md p-3 flex items-center gap-3 animate-fade-in">
                      <div className={cn("w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0", s.bg, "border", s.border)}>
                        <span className={cn("text-xs font-semibold", s.text)}>{r.days_to_limit}d</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-200 font-mono">{r.node}</p>
                        <p className="text-[10px] text-slate-500">{r.resource} at {r.current_percent}% → predicted full by {r.predicted_date}</p>
                      </div>
                      <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase flex-shrink-0", s.text, s.bg, s.border)}>
                        {r.severity}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Node Risks */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Activity size={14} className="text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Node Risk Assessment
              </h3>
            </div>
            {node_risks.length === 0 ? (
              <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 text-center text-sm text-emerald-400">
                <CheckCircle size={16} className="mx-auto mb-1" />
                All nodes within safe thresholds
              </div>
            ) : (
              <div className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-800">
                      {["Node", "CPU", "Memory", "Risk", "Status"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {node_risks.map((n) => {
                      const s = severityStyle[n.severity] || severityStyle.low;
                      return (
                        <tr key={n.node} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors">
                          <td className="px-3 py-2.5 text-slate-300 font-mono text-[10px] max-w-[120px] truncate">{n.node.replace(/^(gke|eks|k3s)-/, "")}</td>
                          <td className="px-3 py-2.5 font-mono" style={{ color: n.cpu_percent > 85 ? "#f87171" : "#34d399" }}>{n.cpu_percent?.toFixed(0)}%</td>
                          <td className="px-3 py-2.5 font-mono" style={{ color: n.memory_percent > 90 ? "#f87171" : "#34d399" }}>{n.memory_percent?.toFixed(0)}%</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-10 h-1 bg-slate-800 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${n.risk_score}%`, backgroundColor: s.bar }} />
                              </div>
                              <span className={cn("font-mono", s.text)}>{n.risk_score}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={cn("text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase", s.text, s.bg, s.border)}>
                              {n.severity}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Anomaly Detection */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-purple-400" />
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Anomaly Detection</h3>
          {anomalies.length > 0 && (
            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-purple-400/10 text-purple-400 border border-purple-400/20">{anomalies.length}</span>
          )}
        </div>
        {anomalies.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 text-center">
            <CheckCircle size={20} className="mx-auto mb-2 text-emerald-400" />
            <p className="text-sm text-emerald-400">No anomalies detected</p>
            <p className="text-xs text-slate-500 mt-0.5">Cluster behavior is within expected patterns</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {anomalies.map((a) => <AnomalyCard key={a.id} anomaly={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}
